import { Box, Group, Stack, Table as ChakraTable, Tabs } from '@chakra-ui/react'
import type { FC } from 'react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import MetricCreateButton from './metricCreateButton'
import MetricCSVModal from './metricCSVModal'

import { Button } from '@app/components/ui/button'
import { EmptyState } from '@app/components/ui/empty-state'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import MetricDate from '@app/pages/metrics/components/columns/metricDate'
import MetricValue from '@app/pages/metrics/components/columns/metricValue'
import MetricDataPointListActions, {
  MetricDataPointListActionsHeader
} from '@app/pages/metrics/metricDataPoints/components/metricDataPointListActions'
import Can from '@app/shared/authorization/can'
import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import SuspenseLoader from '@app/shared/loaders/suspenseLoader'
import Pagination from '@app/shared/pagination'
import Table from '@app/shared/table'
import type { Column } from '@app/shared/table/types'
import type { DomainMetric } from '@app/types'
import type { User as UserType, MetricDataPoint as MetricDataPointType } from '@graphql/types'

type MetricDataPoint = Required<
  Pick<MetricDataPointType, 'id' | 'date' | 'value' | 'segmentFilters' | 'metric' | 'metricId'>
>

type User = Pick<UserType, 'abilities'>

const columns = (metric: DomainMetric, user: User) => {
  const basic: Column<MetricDataPoint>[] = [
    {
      header: 'Date',
      cell: MetricDate
    },
    {
      header: 'Value',
      cell: MetricValue
    }
  ]

  const abilities = abilitiesFor(user)

  if (abilities.can('update', 'metricDataPoint')) {
    basic.push({
      header: (
        <ChakraTable.ColumnHeader>
          <MetricDataPointListActionsHeader metric={metric} />
        </ChakraTable.ColumnHeader>
      ),
      cell: MetricDataPointListActions
    })
  }

  return basic
}

interface Props {
  metric?: DomainMetric
  collection?: MetricDataPoint[]
  metadata?: object
  loading?: boolean
}

const DataTable: FC<Props> = ({ metric = null, collection = [], metadata = {}, loading = false }) => {
  const { user } = useStoreCurrentUser()
  const { strategyId } = useParams()
  const [open, setOpen] = useState(false)

  // If we have a strategyId, we're in a strategy context and should use the lazyload-container from the drawer.
  // Otherwise, we're in the main metrics page and should use the main container.
  const scrollContainer = strategyId ? 'lazyload-container' : 'main'

  if (loading) {
    return (
      <Box p={8}>
        <SuspenseLoader />
      </Box>
    )
  }

  if (!collection.length) {
    return (
      <EmptyState title="No data points yet" description="Add some data points and they will show up here.">
        <Group orientation={{ base: 'vertical', '2xl': 'horizontal' }}>
          <Can I="create" a="metricDataPoint">
            <MetricCreateButton text="Create your first one" variant="subtle" size="sm" />
            <MetricCSVModal value="csv_modal" metric={metric} open={open} setOpen={setOpen} />
            <Button variant="subtle" size="sm" onClick={() => setOpen(true)}>
              Upload CSV
            </Button>
          </Can>
          <Can I="create" a="metricSource">
            <Button asChild variant="subtle" size="sm">
              <Tabs.Trigger value="source" asChild>
                <Link relative="path" to="../source">
                  Connect a data source
                </Link>
              </Tabs.Trigger>
            </Button>
          </Can>
        </Group>
      </EmptyState>
    )
  }

  const data = collection
    .map((mdp) => ({ ...mdp, metric }))
    .sort((a, b) => {
      const dateComparison = new Date(b.date).getTime() - new Date(a.date).getTime()

      if (dateComparison !== 0) {
        return dateComparison
      }

      return Number(b.id ?? 0) - Number(a.id ?? 0)
    })

  return (
    <Stack gap={4}>
      <Table columns={columns(metric, user)} data={data} />
      <Pagination
        px={6}
        pb={4}
        {...{ ...metadata }}
        scrollContainer={scrollContainer}
        showPageButtons={false}
        showPaginationSummary
      />
    </Stack>
  )
}

export default DataTable
